"use client";

import { useState } from "react";

interface Props {
  title: string;
  slug: string;
}

const baseUrl = "https://flowcloser.com";

export default function ArticleShare({ title, slug }: Props) {
  const [copied, setCopied] = useState(false);
  const url = `${baseUrl}/articles/${slug}`;
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  const lineHref = `https://social-plugins.line.me/lineit/share?url=${encodedUrl}`;
  const xHref = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore — older browsers
    }
  };

  const btn: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 999,
    border: "1px solid var(--line)",
    background: "#fff",
    color: "var(--ink-2)",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
    cursor: "pointer",
    transition: "all .15s ease",
  };

  return (
    <div
      style={{
        marginTop: 40,
        paddingTop: 24,
        borderTop: "1px solid var(--line)",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 10,
      }}
    >
      <span style={{ fontSize: 13, color: "var(--ink-3)", marginRight: 6 }}>
        แชร์บทความนี้
      </span>
      <a href={fbHref} target="_blank" rel="noopener noreferrer" style={btn} aria-label="แชร์ Facebook">
        <FacebookIcon /> Facebook
      </a>
      <a href={lineHref} target="_blank" rel="noopener noreferrer" style={btn} aria-label="แชร์ LINE">
        <LineIcon /> LINE
      </a>
      <a href={xHref} target="_blank" rel="noopener noreferrer" style={btn} aria-label="แชร์ X">
        <XIcon /> X
      </a>
      <button type="button" onClick={handleCopy} style={btn} aria-label="คัดลอกลิงก์">
        <LinkIcon /> {copied ? "คัดลอกแล้ว" : "คัดลอกลิงก์"}
      </button>
    </div>
  );
}

const FacebookIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073c0 6.025 4.388 11.022 10.125 11.927v-8.437H7.078V12.07h3.047V9.413c0-3.012 1.793-4.668 4.532-4.668 1.312 0 2.686.235 2.686.235v2.969h-1.514c-1.49 0-1.953.928-1.953 1.879v2.255h3.328l-.532 3.493h-2.796V24c5.736-.905 10.124-5.902 10.124-11.927Z" />
  </svg>
);

const LineIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#06C755">
    <path d="M24 10.304c0-5.369-5.383-9.738-12-9.738S0 4.935 0 10.304c0 4.815 4.269 8.846 10.036 9.608.391.084.923.258 1.058.592.121.303.079.778.039 1.085 0 0-.141.847-.171 1.027-.053.303-.242 1.186 1.038.647 1.281-.54 6.911-4.069 9.428-6.967 1.738-1.906 2.572-3.842 2.572-5.992Zm-18.142 2.87a.233.233 0 0 1-.233.232H2.279a.233.233 0 0 1-.233-.232V8.998a.233.233 0 0 1 .233-.232h.84a.233.233 0 0 1 .232.232v3.108h1.974c.128 0 .233.104.233.233v.835Zm2.012 0a.233.233 0 0 1-.233.232h-.84a.233.233 0 0 1-.233-.232V8.998a.233.233 0 0 1 .233-.232h.84a.233.233 0 0 1 .233.232v4.176Zm4.625 0a.233.233 0 0 1-.233.232h-.84a.232.232 0 0 1-.187-.094l-1.913-2.582v2.444a.233.233 0 0 1-.233.232h-.84a.233.233 0 0 1-.233-.232V8.998a.233.233 0 0 1 .233-.232h.84c.073 0 .143.036.187.094l1.913 2.582V8.998a.233.233 0 0 1 .233-.232h.84a.233.233 0 0 1 .233.232v4.176Zm3.717-3.34a.233.233 0 0 1-.233.232h-1.974v.762h1.974c.128 0 .233.104.233.232v.835a.233.233 0 0 1-.233.233h-1.974v.762h1.974c.128 0 .233.104.233.232v.835a.233.233 0 0 1-.233.232h-3.046a.233.233 0 0 1-.233-.232V8.998a.233.233 0 0 1 .233-.232h3.046a.233.233 0 0 1 .233.232v.835Z" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
